/**
 * Billing details — the name, GSTIN and address printed on the customer's
 * bills. Lives on the profile page's Billing tab.
 *
 * The state decides which GST applies (CGST + SGST within the state, IGST
 * otherwise), so it must be one of the values from GET /states. GSTIN is
 * optional and uppercased before sending. Empty fields go to the backend
 * as null.
 */
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, MapPin, ReceiptText } from 'lucide-react';
import { isApiError } from '@finmin/api-client';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorMessage,
  FancySelect,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Skeleton,
  Textarea,
  toast,
} from '@finmin/ui';
import {
  GSTIN_PATTERN,
  PINCODE_PATTERN,
  useBillingDetails,
  useBillingStates,
  useUpdateBillingDetails,
  type BillingDetails,
  type BillingDetailsUpdate,
} from '@/api/billing';

// ── Schema ────────────────────────────────────────────────────────────────────

function makeSchema(states: string[]) {
  return z.object({
    legal_name: z
      .string()
      .trim()
      .min(1, 'Enter the name to print on your bills.')
      .max(200, 'Keep it under 200 characters.'),
    gstin: z
      .string()
      .trim()
      .transform((v) => v.toUpperCase())
      .refine((v) => v === '' || GSTIN_PATTERN.test(v), 'Enter a valid 15-character GSTIN.'),
    address: z.string().trim().max(500, 'Keep it under 500 characters.'),
    city: z.string().trim().max(100, 'Keep it under 100 characters.'),
    state: z
      .string()
      .min(1, 'Select your state.')
      .refine((v) => states.length === 0 || states.includes(v), 'Select a state from the list.'),
    pincode: z
      .string()
      .trim()
      .refine((v) => v === '' || PINCODE_PATTERN.test(v), 'Enter a valid 6-digit pincode.'),
  });
}

export type BillingDetailsInput = z.input<ReturnType<typeof makeSchema>>;
type Output = z.output<ReturnType<typeof makeSchema>>;

function defaultsFrom(details: BillingDetails | undefined): BillingDetailsInput {
  return {
    legal_name: details?.legal_name ?? '',
    gstin: details?.gstin ?? '',
    address: details?.address ?? '',
    city: details?.city ?? '',
    state: details?.state ?? '',
    pincode: details?.pincode ?? '',
  };
}

function toPayload(values: Output): BillingDetailsUpdate {
  const orNull = (v: string) => (v === '' ? null : v);
  return {
    legal_name: orNull(values.legal_name),
    gstin: orNull(values.gstin),
    address: orNull(values.address),
    city: orNull(values.city),
    state: orNull(values.state),
    pincode: orNull(values.pincode),
  };
}

// ── Tab ───────────────────────────────────────────────────────────────────────

export function BillingDetailsTab() {
  const details = useBillingDetails();
  const states = useBillingStates();

  if (details.isLoading || states.isLoading) {
    return <Skeleton className="h-96 w-full rounded-xl" />;
  }

  const loadError = details.error ?? states.error;

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 font-display text-lg">
          <ReceiptText className="h-4 w-4 text-muted-foreground" aria-hidden />
          Billing details
        </CardTitle>
        <CardDescription>
          These details go on your bills. Your state decides the GST on each bill: CGST and
          SGST within the same state, IGST otherwise.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadError ? (
          <ErrorMessage message={loadError.message || 'Could not load your billing details.'} />
        ) : (
          <BillingDetailsForm details={details.data} states={states.data ?? []} />
        )}
      </CardContent>
    </Card>
  );
}

function BillingDetailsForm({
  details,
  states,
}: {
  details: BillingDetails | undefined;
  states: string[];
}) {
  const schema = React.useMemo(() => makeSchema(states), [states]);
  const update = useUpdateBillingDetails();
  const [apiError, setApiError] = React.useState<string | null>(null);

  const form = useForm<BillingDetailsInput, unknown, Output>({
    resolver: zodResolver(schema),
    defaultValues: defaultsFrom(details),
    mode: 'onTouched',
  });

  const stateOptions = React.useMemo(() => states.map((s) => ({ value: s, label: s })), [states]);

  function onSubmit(values: Output) {
    setApiError(null);
    update.mutate(toPayload(values), {
      onSuccess: (saved) => {
        form.reset(defaultsFrom(saved));
        toast.success('Billing details saved');
      },
      onError: (err) => {
        setApiError(
          isApiError(err) && err.message
            ? err.message
            : 'Could not save your billing details. Please try again.',
        );
      },
    });
  }

  const saving = update.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <FormField
          control={form.control}
          name="legal_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Legal or business name</FormLabel>
              <FormControl>
                <Input {...field} className="h-11" autoComplete="organization" disabled={saving} />
              </FormControl>
              <FormDescription>Your full name, or your business name if you bill a business.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gstin"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                GSTIN <span className="font-normal text-muted-foreground">(optional)</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  className="h-11 font-mono uppercase tracking-wider"
                  maxLength={15}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="22AAAAA0000A1Z5"
                  disabled={saving}
                />
              </FormControl>
              <FormDescription>For businesses. Your GSTIN is printed on your invoices.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Textarea {...field} rows={3} autoComplete="street-address" disabled={saving} />
              </FormControl>
              <FormDescription>Printed on bills of ₹50,000 or more.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input {...field} className="h-11" autoComplete="address-level2" disabled={saving} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pincode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pincode</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                    className="h-11 tabular-nums"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="postal-code"
                    disabled={saving}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="state"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>State</FormLabel>
              <FormControl>
                <FancySelect
                  options={stateOptions}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                    field.onBlur();
                  }}
                  invalid={Boolean(fieldState.error)}
                  disabled={saving}
                  searchable
                  leadingIcon={MapPin}
                  placeholder="Select your state"
                  ariaLabel="State"
                  className="[&_button]:min-h-11"
                />
              </FormControl>
              <FormDescription>Decides whether CGST and SGST or IGST applies.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <ErrorMessage message={apiError} />

        <div className="flex justify-end">
          <Button type="submit" className="w-full sm:w-auto" disabled={saving}>
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Saving
              </span>
            ) : (
              'Save details'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
